import Box from "@mui/material/Box"
import { useTheme } from "@mui/material/styles"
import {
    EEmailProviders,
    EmailAPIFactory,
    EmptyContent,
    FullBar,
    getCalendarFormat,
    getDate,
    getStartOfDate,
    Header,
    LoadingContainer,
    SubNav,
    TColors,
    TEmailReport,
    Typography
} from "@cakemail-org/ui-components-v2"
import { ChartData, ChartDataset } from "chart.js"
import { MouseEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Charts } from "../../charts"
import "./styles.scss"
import "../styles.scss"
import { TTrendByProviders, TTrendByProvidersActiveProviders } from "./types"
import { ColorIcon } from "../../ColorIcon"
import { TSubNavItem } from "@cakemail-org/ui-components-v2/dist/esm/components/subNav/types"
import { getAPIDateRange, getIntervalBasedOnTimeRange } from "../../../utils/date"
import { GraphContainer } from "../../graphContainer"
import { EChartExtraColor } from "../../charts/types"
import { Stack } from "@mui/material"
import { TEmailAPIDeliverabilityProviderReports } from "../../../container/emailAPI/containers/types"
import { fillMissingReportIntervals, getProvidersToFetch, returnEmailTagsQuery, returnProviderQuery } from "../../../utils/data"
import { emptyReportLog } from "../../../data/emailAPI"
import { LastXDays } from "../../lastXDays"

export function TrendByProviders({ timeRange, tagsFilter, providerFilter, controlled, controlledProps, filterOperator, refreshKey }: TTrendByProviders) {
    const { t, i18n } = useTranslation()
    const theme = useTheme()

    const [activeMetrics, setActiveMetrics] = useState<TTrendByProvidersActiveProviders>({})
    const [metricType, setMetricType] = useState<keyof TEmailReport>("unsubscribe_rate")

    const [stateMetrics, setStateMetrics] = useState<TEmailAPIDeliverabilityProviderReports>()
    const [stateLoading, setStateLoading] = useState<boolean>(true)
    const [stateError, setStateError] = useState<boolean>(false)

    let defaultProviders: EEmailProviders[] = [EEmailProviders.gmail, EEmailProviders.outlook, EEmailProviders.proton, EEmailProviders.aol, EEmailProviders.yahoo, EEmailProviders.icloud, EEmailProviders.other]

    useEffect(() => {
        //init state
        const initActiveMetrics: TTrendByProvidersActiveProviders = {}
        for (const p of Object.values(EEmailProviders)) {
            initActiveMetrics[p] = true
        }
        setActiveMetrics(initActiveMetrics)
    }, [])

    let loading: boolean = false
    let error: boolean = false
    let metrics: TEmailAPIDeliverabilityProviderReports | undefined = undefined

    // To aliviate multiple same component on a page, will minimize the ammount of api call. 
    // You cannot change the controlled property afterwards
    if (controlled) {
        metrics = controlledProps?.metrics
        loading = controlledProps?.loading || false
        error = controlledProps?.error || false
    } else {
        useEffect(fetchReports, [timeRange, tagsFilter, providerFilter, refreshKey])
        metrics = stateMetrics
        loading = stateLoading
        error = stateError
    }

    function handleActiveMetric(provider: EEmailProviders) {
        setActiveMetrics({
            ...activeMetrics,
            [provider]: !activeMetrics[provider]
        })
    }

    function getDefaultFetchParams() {
        const apiTimeRange = getAPIDateRange({ range: timeRange })
        return {
            tags: returnEmailTagsQuery(tagsFilter, { operator: filterOperator }),
            startTime: apiTimeRange.start,
            endTime: apiTimeRange.end
        }
    }

    function fetchReports() {
        setStateLoading(true)
        const defaultParams = {
            interval: getIntervalBasedOnTimeRange(timeRange),
            ...getDefaultFetchParams()
        }

        const promisses: any[] = []
        const providersToFetch = getProvidersToFetch(providerFilter)

        providersToFetch.forEach((p: EEmailProviders) => {
            promisses.push(EmailAPIFactory.getReport({
                providers: returnProviderQuery({ providers: p, operator: filterOperator }),
                ...defaultParams
            }).then((data) => {
                return {
                    provider: p,
                    data
                }
            }))
        })
        Promise.allSettled(promisses).then((results) => {
            const newMetrics: TEmailAPIDeliverabilityProviderReports = {}
            let hasOnlyErrors = true
            try {
                results.forEach((res) => {
                    if (res.status === "fulfilled") {
                        hasOnlyErrors = false
                        const entry: { provider: EEmailProviders, data: TEmailReport[] } = res.value
                        fillMissingReportIntervals(entry.data, defaultParams.startTime, defaultParams.endTime, defaultParams.interval, emptyReportLog)
                        newMetrics[entry.provider] = entry.data
                    }
                })
            } catch (e) {
                hasOnlyErrors = true
            }

            if (hasOnlyErrors) {
                setStateError(true)
            } else {
                setStateMetrics(newMetrics)
            }

            setStateLoading(false)
        })

    }

    function handleSubNavChange(event: MouseEvent<HTMLButtonElement>, item: TSubNavItem) {
        setMetricType(item.name as keyof TEmailReport)
    }


    const colors: (EChartExtraColor | TColors)[] = [EChartExtraColor.orange, EChartExtraColor.deepBlue, EChartExtraColor.lime, "wildSand", EChartExtraColor.blue, "primary", "secondary", "body1"]

    const navItem: TSubNavItem[] = [
        {
            name: "unsubscribe_rate",
            text: t("general-unsubscribe_rate")
        },
        {
            name: "bounce_rate",
            text: t("general-bounce_rate")
        },
        {
            name: "spam_rate",
            text: t("general-spam_rate")
        },
        {
            name: "reject_rate",
            text: t("general-reject_rate")
        }
    ]

    const labels: any[] = []

    let data: (ChartDataset & { metadata: any })[] = Object.entries(metrics || {}).map(([p, reports], index) => {
        labels.push(<Stack
            className={`pointer ${activeMetrics[p as EEmailProviders] ? "active" : "inactive"}`}
            onClick={() => {
                handleActiveMetric(p as EEmailProviders)
            }}
            key={'engagement-trend-labels' + index}
            direction="row"
            alignItems="center"
        >
            <ColorIcon color={colors[index] ?? "primary"} /><Typography variant="body1S" className="semiBold"> {t(`${p}`)}</Typography>
        </Stack>)
        return {
            metadata: { provider: p },
            type: "line",
            tension: 0.2,
            data: reports.map((r) => {
                return {
                    x: r.interval_start * 1000,
                    y: (r?.[metricType] || 0)
                }
            }),
            borderColor: colors[index] ? (colors[index][0] === "#" ? colors[index] : theme.palette[colors[index] as TColors].main) : theme.palette.primary.main,
        }
    }) || []

    //Keeping the colors and filtering out the non active sets
    data = data.filter((set) => {
        const curPro: EEmailProviders = set.metadata?.provider
        return curPro && activeMetrics[curPro]
    })

    const chartData: ChartData = {
        datasets: data
    }

    const firstProvider = metrics ? Object.keys(metrics)[0] as EEmailProviders : undefined
    const firstProviderMetrics = firstProvider && metrics?.[firstProvider] && metrics[firstProvider]?.length > 0 ? metrics?.[firstProvider] : undefined
    const firstMetric = firstProviderMetrics ? firstProviderMetrics[0] : undefined
    const lastMetric = firstProviderMetrics ? firstProviderMetrics[firstProviderMetrics.length - 1] : undefined
    //Override default options
    const options = {
        scales: {
            x: {
                type: 'time',
                /*time: {
                    unit: 'minute',  // Adjust this to set the interval of labels (e.g., 'minute', 'hour', 'day', 'week', 'month', 'year')
                },*/
                min: firstMetric ? firstMetric.interval_start * 1000 : getStartOfDate({ add: -timeRange, inMiliseconds: true }),
                max: lastMetric ? lastMetric.interval_start * 1000 : getDate({ format: "unix" }) * 1000,
                ticks: {
                    //autoSkip: false,
                    maxTicksLimit: 12,
                    callback: function (value: number) {
                        return getCalendarFormat({ unixDate: Number(value) / 1000 })
                    }
                }
            },
            y1: undefined
        }
    }

    const apiTimeRange = getAPIDateRange({ range: timeRange })
    return <Stack className="analyticBlock engagementTrends">
        <Header
            heading={t("header-negative-trends-heading")}
            postHeading={<LastXDays apiTimeRange={apiTimeRange} />}
            headingTooltip={t("header-negative-trends-heading-tooltip")}
        />

        <LoadingContainer loading={!metrics && !error} variant="linear">
            {metrics && <GraphContainer loading={loading}>
                <FullBar
                    left={<SubNav
                        items={navItem}
                        onClick={handleSubNavChange}
                    />}
                    right={labels}
                />

                {/*@ts-ignore*/}
                <Charts type="line" data={chartData} height={"18rem"} options={options} />
            </GraphContainer>}
            {error && <EmptyContent
                title={t("analytic-block-loading-error-title")}
            />}
        </LoadingContainer>
    </Stack>
}